import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import Sidebar from '../components/Sidebar';
import BadgeChip from '../components/BadgeChip';
import { OttoLoading } from '../components/otto';
import { fmtDate } from '../lib/dates';
import { formatGBP as gbp } from '../lib/money';
import { fmtBudgetRange, budgetPosition } from '../lib/budget';
import { fetchBlockingComplianceDocs } from '../lib/supplierComplianceReasons';

// Side-by-side comparison view for a brief's responses. Mounts at
// /briefs/:id/compare. Pulls the brief + its recipients + responses and
// lays every responded supplier out as a column across structured
// attribute rows. Columns scroll horizontally with the attribute-label
// column pinned (compare-quotes brief §10), so a busy brief with many
// responses stays legible.
//
// The award decision happens from the sticky bar (§9), not per-column at
// the bottom of a long scroll. A non-compliant supplier is blocked from
// award (§1); an admin can override with a typed reason that writes a
// compliance_audit_log entry.
//
// Compliance state is taken from the trusted suppliers.compliance_status
// column (what SupplierDetail + the compliance dashboard render) so it
// matches everywhere by construction; the per-document "why" comes from
// fetchBlockingComplianceDocs, which reuses src/lib/compliance.js — not a
// fresh derivation.

const formatGBP = (n) => gbp(n, { pence: 'auto', fallback: '£0' });
const formatDate = (iso) => fmtDate(iso);

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const monthYear = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return `${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
};

// Caveats live in the free-text `comments` field (there's no structured
// caveats column). Count discrete lines as a pragmatic caveat count for
// the header chip — one blob is one caveat, a bulleted list counts each.
const caveatCount = (comments) =>
  comments ? comments.split(/\r?\n/).map(s => s.trim()).filter(Boolean).length : 0;

// Compliance chip model — text carries the state (never colour alone, §11).
// Only red blocks award; amber (expiring soon) warns without blocking.
function complianceChip(status) {
  if (status === 'red')   return { label: 'Compliance: blocked',       tone: 'red',   block: true };
  if (status === 'amber') return { label: 'Compliance: expiring soon', tone: 'amber', block: false };
  if (status === 'green') return { label: 'Compliance: green',         tone: 'green', block: false };
  return { label: 'Compliance: not on file', tone: 'grey', block: false };
}
const CHIP_TONE = {
  red:   'bg-[#E0290A]/12 text-[#B71F06] border-[#E0290A]/30',
  amber: 'bg-[#E89A2E]/15 text-[#9A6206] border-[#E89A2E]/40',
  green: 'bg-[#92D400]/12 text-fusion-forest border-[#92D400]/30',
  grey:  'bg-[#9BA08C]/12 text-[#51534A] border-[#9BA08C]/30',
};

// §3 deposit-trigger labels (mirrors SupplierResponse). 'other' carries no
// suffix — the free-text line usually explains it.
const TRIGGER_LABEL = { on_booking: 'on booking', on_order: 'on order', other: '' };

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// Plain-text summary of a response's structured payment terms, for export.
function paymentTermsText(response) {
  const parts = [];
  if (response.deposit_pct != null && response.deposit_pct !== '') {
    const trig = response.deposit_trigger && TRIGGER_LABEL[response.deposit_trigger] ? ` ${TRIGGER_LABEL[response.deposit_trigger]}` : '';
    parts.push(`${Number(response.deposit_pct)}% deposit${trig}${Number(response.deposit_pct) >= 100 ? ' (full payment up front)' : ''}`);
  }
  if (response.balance_terms) parts.push(`balance ${response.balance_terms}`);
  if (response.penalty_pct_per_day != null && response.penalty_pct_per_day !== '' && Number(response.penalty_pct_per_day) > 0) {
    const r = Number(response.penalty_pct_per_day);
    parts.push(`${r}% per day late (up to ~${Math.round(r * 30)}% if a month late)`);
  }
  if (response.payment_schedule) parts.push(response.payment_schedule);
  return parts.join(' · ') || '—';
}

// §13 — build a full INTERNAL decision record as a self-contained print
// document (compliance state, caveats and working notes included). Opened in
// a new window for the browser's Save-as-PDF; no PDF dependency, and clearly
// marked not-for-client since it carries internal notes.
function buildComparisonExportHtml({ brief, pairs, cheapestPrice, reasonsBySupplier, notesBySupplier, trackBySupplier, parkedIds, headerLine }) {
  const cards = pairs.map(({ recipient, response }) => {
    const s = recipient.suppliers || {};
    const sid = recipient.supplier_id;
    const price = response.total_price;
    const vatRate = Number(response.vat_rate) || 0;
    const gross = (Number(price) || 0) * (1 + vatRate / 100);
    const pos = budgetPosition(price, brief);
    const chip = complianceChip(s.compliance_status);
    const reasons = reasonsBySupplier.get(sid) || [];
    const note = notesBySupplier.get(sid)?.working_note;
    const track = trackBySupplier.get(sid);
    const groupPos = (cheapestPrice != null && price != null)
      ? (Number(price) === cheapestPrice ? 'Cheapest' : `+${formatGBP(Number(price) - cheapestPrice)} vs cheapest`)
      : '';
    const contextBits = [pos ? `${pos.pct}% ${pos.direction} budget` : '', groupPos].filter(Boolean).join(' · ');
    const row = (label, value) => `<tr><th>${label}</th><td>${value}</td></tr>`;
    return `
      <section class="card ${parkedIds.has(sid) ? 'parked' : ''}">
        <h2>${esc(s.supplier_name || 'Supplier')}${parkedIds.has(sid) ? ' <span class="tag">Parked</span>' : ''}</h2>
        <p class="sub">${track && track.count > 0 ? `Worked with us ${track.count} time${track.count === 1 ? '' : 's'}` : 'First time working with us'}${s.main_contact_name ? ` · ${esc(s.main_contact_name)}` : ''}</p>
        <p class="price">${price != null ? formatGBP(price) : '—'} <span>ex-VAT</span> &nbsp;·&nbsp; ${gross ? formatGBP(gross) : '—'} <span>inc VAT (${vatRate}%)</span></p>
        ${contextBits ? `<p class="context">${esc(contextBits)}</p>` : ''}
        ${response.amended_pending ? `<p class="context" style="color:#B71F06;font-weight:600;">⚠ Quoted before the brief was amended${response.amended_since ? ` on ${esc(fmtDate(response.amended_since))}` : ''} — priced against an earlier version, not directly comparable.</p>` : ''}
        <table>
          ${row('Compliance', `<span class="c-${chip.tone}">${esc(chip.label)}</span>${reasons.length ? ` — ${esc(reasons.map(r => r.detail).join('; '))}` : ''}`)}
          ${row('Payment terms', esc(paymentTermsText(response)))}
          ${row('Caveats', response.comments ? esc(response.comments) : 'None')}
          ${row('Briefs won', track?.count ? String(track.count) : 'None yet')}
          ${row('Confirmation required by', response.deadline_date ? esc(fmtDate(response.deadline_date)) : '—')}
          ${row('Working note (internal)', note ? esc(note) : '—')}
        </table>
      </section>`;
  }).join('');

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(brief?.brief_name || 'Quote comparison')} — internal comparison</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #17170f; margin: 32px; }
      .head { border-bottom: 2px solid #92D400; padding-bottom: 12px; margin-bottom: 20px; }
      .eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #009159; margin: 0 0 4px; }
      h1 { font-size: 22px; margin: 0 0 4px; }
      .meta { font-size: 12px; color: #51534A; margin: 0; }
      .internal { margin: 10px 0 0; font-size: 11px; font-weight: 700; color: #B71F06; }
      .card { border: 1px solid #E6E9DA; border-radius: 10px; padding: 16px; margin-bottom: 14px; page-break-inside: avoid; }
      .card.parked { opacity: 0.55; }
      .card h2 { font-size: 16px; margin: 0 0 2px; }
      .tag { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #51534A; border: 1px solid #9BA08C; border-radius: 999px; padding: 1px 7px; }
      .sub { font-size: 11px; color: #51534A; margin: 0 0 8px; }
      .price { font-size: 18px; font-weight: 800; margin: 0 0 2px; }
      .price span { font-size: 11px; font-weight: 400; color: #51534A; }
      .context { font-size: 12px; color: #51534A; margin: 0 0 10px; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th, td { text-align: left; vertical-align: top; padding: 5px 8px; border-top: 1px solid #EEF0E6; }
      th { width: 190px; font-weight: 700; color: #51534A; }
      .c-red { color: #B71F06; font-weight: 700; } .c-amber { color: #9A6206; font-weight: 700; }
      .c-green { color: #2C6E2C; font-weight: 700; } .c-grey { color: #51534A; }
      @media print { body { margin: 0; } }
    </style></head><body>
    <div class="head">
      <p class="eyebrow">Internal quote comparison</p>
      <h1>${esc(brief?.brief_name || 'Brief')}</h1>
      <p class="meta">${esc(brief?.fusion_project_number || '—')}${brief?.fusion_project_name ? ` · ${esc(brief.fusion_project_name)}` : ''}${headerLine ? ` · ${esc(headerLine)}` : ''}</p>
      <p class="internal">Internal decision record — includes compliance, caveats and working notes. Not for client distribution.</p>
    </div>
    ${cards}
  </body></html>`;
}

export default function ComparisonView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentInternalUser } = useCurrentInternalUser();

  const [brief, setBrief] = useState(null);
  const [recipients, setRecipients] = useState([]); // ALL recipients — for the §8 header picture
  const [pairs, setPairs] = useState([]);           // responded recipient+response pairs
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('price');
  const [highlightDifferences, setHighlightDifferences] = useState(true); // §6 — on by default
  const [badgesBySupplier, setBadgesBySupplier] = useState(new Map());
  const [badgeCategoryMap, setBadgeCategoryMap] = useState(new Map());
  const [reasonsBySupplier, setReasonsBySupplier] = useState(new Map()); // §1 — red suppliers only
  const [trackBySupplier, setTrackBySupplier] = useState(new Map());     // §5 — { count, lastAwardAt }
  const [notesBySupplier, setNotesBySupplier] = useState(new Map());     // §7 — { working_note, parked_at }
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);    // §9 — sticky award bar
  const [overriddenIds, setOverriddenIds] = useState(() => new Set());   // §1 — overridden this session
  const [overrideDraft, setOverrideDraft] = useState(null);              // { supplierId, reason }
  const [savingOverride, setSavingOverride] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [briefRes, recipientsRes, responsesRes] = await Promise.all([
        supabase
          .from('briefs')
          .select('id, brief_name, fusion_project_number, fusion_project_name, status, awarded_supplier_id, awarded_amount, minimum_budget, maximum_budget, current_version')
          .eq('id', id)
          .single(),
        supabase
          .from('brief_recipients')
          .select('id, status, supplier_id, last_seen_version, amend_pending_since, suppliers(id, supplier_name, category, main_contact_name, main_contact_email, compliance_status, risk_flag, rating_general)')
          .eq('brief_id', id),
        supabase
          .from('brief_responses')
          .select('*, line_items:brief_response_line_items(*), attachments:brief_response_attachments(*)')
          .eq('brief_id', id)
          .eq('is_submitted', true),
      ]);
      if (cancelled) return;
      const recips = recipientsRes.data || [];
      const responses = responsesRes.data || [];
      const next = [];
      for (const r of recips) {
        if (r.status !== 'responded') continue;
        const response = responses.find(rr => rr.supplier_id === r.supplier_id);
        if (!response) continue;
        next.push({ recipient: r, response });
      }
      setBrief(briefRes.data || null);
      setRecipients(recips);
      setPairs(next);

      // Default the sticky-bar selection to the cheapest supplier that
      // isn't compliance-blocked; fall back to the cheapest overall.
      const byPrice = [...next].sort(
        (a, b) => (Number(a.response.total_price) || Infinity) - (Number(b.response.total_price) || Infinity)
      );
      const firstOk = byPrice.find(p => (p.recipient.suppliers?.compliance_status) !== 'red');
      setSelectedSupplierId((firstOk || byPrice[0])?.recipient.supplier_id || null);

      const supplierIds = next.map(p => p.recipient.supplier_id).filter(Boolean);
      if (supplierIds.length > 0) {
        const [badgesRes, catsRes, awardsRes, notesRes] = await Promise.all([
          supabase.from('supplier_badges').select('supplier_id, category_id, tier, expires_at').in('supplier_id', supplierIds),
          supabase.from('badge_categories').select('id, slug, name'),
          // §5 track record — briefs previously awarded to these suppliers
          // (excluding this one). Count + most-recent award date per supplier.
          supabase.from('briefs').select('awarded_supplier_id, closed_at').in('awarded_supplier_id', supplierIds).neq('id', id),
          // §7 internal working notes + parking (staff-only table).
          supabase.from('brief_response_admin_notes').select('supplier_id, working_note, parked_at').eq('brief_id', id).in('supplier_id', supplierIds),
        ]);
        if (cancelled) return;
        const now = Date.now();
        const grouped = new Map();
        for (const b of badgesRes.data || []) {
          if (b.expires_at && new Date(b.expires_at).getTime() < now) continue;
          if (!grouped.has(b.supplier_id)) grouped.set(b.supplier_id, []);
          grouped.get(b.supplier_id).push(b);
        }
        setBadgesBySupplier(grouped);
        setBadgeCategoryMap(new Map((catsRes.data || []).map(c => [c.id, c])));

        const track = new Map();
        for (const b of awardsRes.data || []) {
          const t = track.get(b.awarded_supplier_id) || { count: 0, lastAwardAt: null };
          t.count += 1;
          if (b.closed_at && (!t.lastAwardAt || b.closed_at > t.lastAwardAt)) t.lastAwardAt = b.closed_at;
          track.set(b.awarded_supplier_id, t);
        }
        setTrackBySupplier(track);
        setNotesBySupplier(new Map((notesRes.data || []).map(n => [n.supplier_id, { working_note: n.working_note, parked_at: n.parked_at }])));

        // §1 reasons — only fetch the per-doc "why" for suppliers the
        // trusted column already flags red. Small N (the compare cohort).
        const reds = next.filter(p => (p.recipient.suppliers?.compliance_status) === 'red');
        const reasonEntries = await Promise.all(reds.map(async (p) => {
          const sup = p.recipient.suppliers || {};
          const blocking = await fetchBlockingComplianceDocs(p.recipient.supplier_id, sup.risk_flag);
          return [p.recipient.supplier_id, blocking];
        }));
        if (cancelled) return;
        setReasonsBySupplier(new Map(reasonEntries));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Sort — show ALL responded suppliers (the pinned column + horizontal
  // scroll handle width, §10), no cap.
  const sortedPairs = useMemo(() => {
    const list = [...pairs];
    if (sortBy === 'price') {
      list.sort((a, b) => (Number(a.response.total_price) || Infinity) - (Number(b.response.total_price) || Infinity));
    } else if (sortBy === 'earliest') {
      list.sort((a, b) => new Date(a.response.deadline_date || 0) - new Date(b.response.deadline_date || 0));
    } else if (sortBy === 'submitted') {
      list.sort((a, b) => new Date(a.response.submitted_at || a.response.created_at || 0) - new Date(b.response.submitted_at || b.response.created_at || 0));
    }
    return list;
  }, [pairs, sortBy]);

  const cheapestPrice = useMemo(() => {
    let lowest = Infinity;
    for (const { response } of sortedPairs) {
      const n = Number(response.total_price);
      if (isFinite(n) && n < lowest) lowest = n;
    }
    return isFinite(lowest) ? lowest : null;
  }, [sortedPairs]);
  const cheapestSupplierId = useMemo(() => {
    let lowest = null;
    for (const { recipient, response } of sortedPairs) {
      const n = Number(response.total_price);
      if (!isFinite(n)) continue;
      if (lowest == null || n < lowest.value) lowest = { value: n, id: recipient.supplier_id };
    }
    return lowest?.id || null;
  }, [sortedPairs]);
  const earliestSupplierId = useMemo(() => {
    let earliest = null;
    for (const { recipient, response } of sortedPairs) {
      if (!response.deadline_date) continue;
      const t = new Date(response.deadline_date).getTime();
      if (isNaN(t)) continue;
      if (earliest == null || t < earliest.value) earliest = { value: t, id: recipient.supplier_id };
    }
    return earliest?.id || null;
  }, [sortedPairs]);

  const handleAward = useCallback((supplierId, overrideReason = null) => {
    // §7 — if there's a working note for this supplier, offer it as the
    // starting text for the award decision reason rather than an empty box.
    const seed = notesBySupplier.get(supplierId)?.working_note?.trim() || null;
    navigate(`/briefs/${id}`, {
      state: {
        openCompletePanel: true,
        awardSupplierId: supplierId,
        ...(overrideReason ? { complianceOverrideReason: overrideReason } : {}),
        ...(seed ? { decisionReasonSeed: seed } : {}),
      },
    });
  }, [navigate, id, notesBySupplier]);

  // §7 — persist a working note / park state (staff-only table), optimistic.
  const saveAdminNote = useCallback(async (supplierId, patch) => {
    const cur = notesBySupplier.get(supplierId) || {};
    const merged = { ...cur, ...patch };
    setNotesBySupplier(prev => new Map(prev).set(supplierId, merged));
    const { error } = await supabase.from('brief_response_admin_notes').upsert({
      brief_id: id,
      supplier_id: supplierId,
      working_note: merged.working_note ?? null,
      parked_at: merged.parked_at ?? null,
      updated_by: currentInternalUser?.id || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'brief_id,supplier_id' });
    if (error) {
      // eslint-disable-next-line no-alert
      window.alert(`Couldn't save the note — ${error.message}`);
    }
  }, [id, notesBySupplier, currentInternalUser]);

  const togglePark = useCallback((supplierId) => {
    const parked = notesBySupplier.get(supplierId)?.parked_at;
    saveAdminNote(supplierId, { parked_at: parked ? null : new Date().toISOString() });
  }, [notesBySupplier, saveAdminNote]);

  const parkedIds = useMemo(
    () => new Set([...notesBySupplier].filter(([, v]) => v.parked_at).map(([k]) => k)),
    [notesBySupplier]
  );

  // §1 override — capture a reason, write the audit entry, then proceed to
  // the award flow. The audit row is the point of the override, so if the
  // insert fails we surface it and do NOT unlock the award.
  const confirmOverride = useCallback(async () => {
    if (!overrideDraft || !overrideDraft.reason.trim()) return;
    const supplierId = overrideDraft.supplierId;
    const reason = overrideDraft.reason.trim();
    setSavingOverride(true);
    const { error } = await supabase.from('compliance_audit_log').insert({
      action: 'override',
      supplier_id: supplierId,
      actor_id: currentInternalUser?.id || null,
      document_key: null,
      meta: { type: 'award_compliance_override', brief_id: id, reason },
    });
    setSavingOverride(false);
    if (error) {
      // eslint-disable-next-line no-alert
      window.alert(`Couldn't record the compliance override — ${error.message}. The award was not unlocked.`);
      return;
    }
    setOverriddenIds(prev => new Set(prev).add(supplierId));
    setOverrideDraft(null);
    handleAward(supplierId, reason);
  }, [overrideDraft, currentInternalUser, id, handleAward]);

  const isAwarded = brief?.status === 'closed' && !!brief?.awarded_supplier_id;
  const canAward = isAdmin(currentInternalUser);

  // §8 header — responses against invitations, plus budget.
  const invited = recipients.length;
  const respondedCount = pairs.length;
  const declinedCount = recipients.filter(r => r.status === 'declined').length;
  const noResponse = Math.max(0, invited - respondedCount - declinedCount);
  const budgetStr = fmtBudgetRange(brief, { withVat: true });

  const selectedPair = sortedPairs.find(p => p.recipient.supplier_id === selectedSupplierId) || null;

  // §13 — full internal PDF via a print window (no PDF dependency).
  const handleExportPdf = () => {
    if (sortedPairs.length === 0) { window.alert('Nothing to export yet — no responses have come in.'); return; }
    const headerLine = `${respondedCount} of ${invited} invited responded`
      + ((declinedCount || noResponse) ? ` · ${declinedCount} declined, ${noResponse} no response` : '')
      + (budgetStr ? ` · Budget ${budgetStr}` : '');
    const html = buildComparisonExportHtml({
      brief, pairs: sortedPairs, cheapestPrice, reasonsBySupplier, notesBySupplier, trackBySupplier, parkedIds, headerLine,
    });
    const w = window.open('', '_blank');
    if (!w) { window.alert('Please allow pop-ups to export the comparison.'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* the user can print manually */ } }, 300);
  };

  return (
    <div className="h-screen bg-canvas flex overflow-hidden font-['Metropolis']">
      <Sidebar currentInternalUser={currentInternalUser} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <span aria-hidden="true" className="h-0.5 flex-shrink-0" style={{ background: 'linear-gradient(90deg, #92D400 0%, #92D400 30%, transparent 30%)' }} />

        {/* Header */}
        <div className="px-8 pt-6 pb-4 flex items-start justify-between gap-6 flex-shrink-0 border-b border-line">
          <div className="min-w-0">
            <p className="text-[10px] font-bold tracking-[2px] uppercase text-fusion-forest mb-1.5">Comparing Quotes</p>
            <h1 className="text-2xl font-bold text-ink mb-1 truncate">{brief?.brief_name || 'Brief'}</h1>
            <p className="text-xs text-[#51534A]">
              {brief?.fusion_project_number || '—'}
              {brief?.fusion_project_name && <> · {brief.fusion_project_name}</>}
            </p>
            {/* §8 — the whole picture, not just "2 of 2". */}
            {!loading && (
              <p className="text-xs text-fusion-mid-grey mt-1.5">
                <span className="font-semibold text-ink">{respondedCount} of {invited}</span> invited responded
                {(declinedCount > 0 || noResponse > 0) && (
                  <> · {declinedCount} declined, {noResponse} no response</>
                )}
                {budgetStr && <> · Budget {budgetStr}</>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <button
              type="button"
              onClick={handleExportPdf}
              title="Internal decision record — includes compliance, caveats and working notes"
              className="bg-transparent border border-line text-fusion-mid-grey text-[13px] font-semibold rounded-xl px-4 py-2.5 hover:border-[#DADCCD] transition-colors">
              Export as PDF
            </button>
            <button
              type="button"
              onClick={() => navigate(`/briefs/${id}`)}
              className="bg-white border border-line text-fusion-mid-grey text-[13px] font-semibold rounded-xl px-4 py-2.5 hover:border-active-green hover:text-active-green transition-colors inline-flex items-center gap-2">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back to Brief
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="px-8 py-4 flex items-center gap-3 flex-wrap border-b border-line flex-shrink-0">
          <span className="fusion-field-label">Sort</span>
          <div className="inline-flex items-center bg-white border border-line rounded-xl p-1 gap-0.5">
            {[
              { value: 'price',     label: 'Price ↑' },
              { value: 'earliest',  label: 'Earliest Confirmation' },
              { value: 'submitted', label: 'Submitted ↑' },
            ].map(o => (
              <button
                key={o.value}
                type="button"
                onClick={() => setSortBy(o.value)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold tracking-[2px] uppercase transition-colors ${
                  sortBy === o.value ? 'bg-[#92D400]/15 text-fusion-forest' : 'text-[#51534A] hover:text-ink'
                }`}>
                {o.label}
              </button>
            ))}
          </div>
          <span className="text-[#51534A] text-[10px] ml-2">·</span>
          <label className="inline-flex items-center gap-2 cursor-pointer text-[11px] font-bold tracking-[2px] uppercase text-[#51534A] hover:text-fusion-mid-grey">
            <input type="checkbox" checked={highlightDifferences} onChange={(e) => setHighlightDifferences(e.target.checked)} className="w-4 h-4 accent-active-green" />
            Highlight differences
          </label>
        </div>

        {/* Body — the table scrolls both axes; the label column + column
            headers stay pinned. */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="py-20"><OttoLoading size="large" message="Loading comparison" /></div>
          ) : sortedPairs.length === 0 ? (
            <div className="m-8 bg-white border border-line rounded-xl px-8 py-12 text-center">
              <p className="text-sm text-fusion-mid-grey mb-2">Nothing to compare yet.</p>
              <p className="text-xs text-[#51534A] max-w-md mx-auto">
                Suppliers need to submit responses before they appear in the side-by-side view.
                Once one or more come in, this page will populate automatically.
              </p>
            </div>
          ) : (
            <ComparisonTable
              pairs={sortedPairs}
              brief={brief}
              cheapestPrice={cheapestPrice}
              cheapestSupplierId={cheapestSupplierId}
              earliestSupplierId={earliestSupplierId}
              awardedSupplierId={brief?.awarded_supplier_id || null}
              selectedSupplierId={selectedSupplierId}
              onSelect={setSelectedSupplierId}
              highlight={highlightDifferences}
              briefId={id}
              badgesBySupplier={badgesBySupplier}
              badgeCategoryMap={badgeCategoryMap}
              reasonsBySupplier={reasonsBySupplier}
              trackBySupplier={trackBySupplier}
              notesBySupplier={notesBySupplier}
              parkedIds={parkedIds}
              isAdminUser={canAward}
              onTogglePark={togglePark}
              onSaveNote={saveAdminNote} />
          )}
        </div>

        {/* §9 — sticky award bar. Always visible; carries the selected
            supplier's price + budget position and the award control. */}
        {canAward && !loading && sortedPairs.length > 0 && selectedPair && (
          <AwardBar
            pair={selectedPair}
            brief={brief}
            cheapestPrice={cheapestPrice}
            isAwarded={isAwarded}
            awardedSupplierId={brief?.awarded_supplier_id || null}
            reasonsBySupplier={reasonsBySupplier}
            overriddenIds={overriddenIds}
            overrideDraft={overrideDraft}
            setOverrideDraft={setOverrideDraft}
            savingOverride={savingOverride}
            onConfirmOverride={confirmOverride}
            onAward={handleAward} />
        )}
      </div>
    </div>
  );
}

// ── Per-quote budget position line (§2) ──────────────────────────────────
function BudgetPositionLine({ price, brief, cheapestPrice }) {
  const pos = budgetPosition(price, brief);
  const p = Number(price);
  const parts = [];
  if (pos) {
    // A quote far under budget is a warning (usually a misread brief), not
    // a win — flag it amber; a modest under / any over reads neutral.
    const flag = pos.direction === 'under' && pos.pct >= 60;
    parts.push(
      <span key="bud" className={flag ? 'text-[#9A6206] font-semibold' : 'text-[#51534A]'}>
        {pos.pct}% {pos.direction} budget{flag ? ' ⚠' : ''}
      </span>
    );
  }
  if (cheapestPrice != null && isFinite(p)) {
    if (p === cheapestPrice) {
      parts.push(<span key="grp" className="text-fusion-forest font-semibold">Cheapest</span>);
    } else {
      parts.push(<span key="grp" className="text-[#51534A]">+{formatGBP(p - cheapestPrice)} vs cheapest</span>);
    }
  }
  if (parts.length === 0) return null;
  return (
    <p className="text-[10.5px] mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 leading-tight">
      {parts.map((node, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span aria-hidden="true" className="text-[#C9CDBC]">·</span>}
          {node}
        </React.Fragment>
      ))}
    </p>
  );
}

// ── Structured payment terms (§3) — compared as values, with the terms
// worth noticing flagged: a deposit at/near 100%, and any per-day penalty
// (its worst case projected over a month late so a small daily rate reads
// as the large number it becomes). Free-text payment_schedule shows beneath
// for anything that didn't fit the structured fields (or legacy quotes). ──
function PaymentTermsCell({ response }) {
  const dep = response.deposit_pct;
  const trig = response.deposit_trigger;
  const bal = response.balance_terms;
  const pen = response.penalty_pct_per_day;
  const schedule = response.payment_schedule;
  const depNum = Number(dep);
  const hasDeposit = dep != null && dep !== '' && isFinite(depNum);
  const bigDeposit = hasDeposit && depNum >= 100;
  const penNum = Number(pen);
  const hasPenalty = pen != null && pen !== '' && isFinite(penNum) && penNum > 0;
  if (!hasDeposit && !bal && !hasPenalty && !schedule) {
    return <span className="text-[#51534A] italic">—</span>;
  }
  return (
    <div className="space-y-1 text-xs">
      {hasDeposit && (
        <p className={bigDeposit ? 'text-[#B71F06] font-semibold' : 'text-fusion-mid-grey'}>
          {bigDeposit && <span aria-hidden="true">⚠ </span>}
          {depNum}% deposit{trig && TRIGGER_LABEL[trig] ? ` ${TRIGGER_LABEL[trig]}` : ''}
          {bigDeposit && <span className="sr-only"> — full payment up front</span>}
        </p>
      )}
      {bal && <p className="text-fusion-mid-grey">Balance {bal}</p>}
      {hasPenalty && (
        <p className="text-[#9A6206] font-semibold">
          <span aria-hidden="true">⚠ </span>{penNum}% per day late
          <span className="font-normal text-[#7A4E10]"> — up to ~{Math.round(penNum * 30)}% if a month late</span>
        </p>
      )}
      {schedule && <p className="text-[#51534A] whitespace-pre-wrap">{schedule}</p>}
    </div>
  );
}

// ── Working note (§7) — internal, per supplier, saved on blur. Its text
// pre-fills the award decision reason when this supplier is awarded. ──
function WorkingNoteCell({ value, onSave }) {
  const [text, setText] = useState(value || '');
  useEffect(() => { setText(value || ''); }, [value]);
  return (
    <textarea
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => { if ((text || '') !== (value || '')) onSave(text.trim() ? text.trim() : ''); }}
      rows={2}
      placeholder="Internal note — only your team sees this. Used to pre-fill the award reason."
      className="w-full bg-[#FBFCF8] border border-line rounded-lg px-2.5 py-1.5 text-xs text-ink placeholder:text-[#9BA08C] focus:outline-none focus:border-active-green resize-y" />
  );
}

// ── The comparison table (§10 pinned column, §11 semantics) ──────────────
function ComparisonTable({
  pairs, brief, cheapestPrice, cheapestSupplierId, earliestSupplierId, awardedSupplierId,
  selectedSupplierId, onSelect, highlight, briefId, badgesBySupplier, badgeCategoryMap,
  reasonsBySupplier, trackBySupplier, notesBySupplier, parkedIds, isAdminUser, onTogglePark, onSaveNote,
}) {
  const labelCol = "sticky left-0 z-10 bg-white w-[210px] min-w-[210px] px-4 py-3 text-left text-[10px] font-bold tracking-[2px] uppercase text-[#51534A] border-b border-r border-line align-top";
  const sectionRow = "bg-[#92D400]/[0.06] border-t border-[#92D400]/30 px-4 py-2 text-[10px] font-bold tracking-[2px] uppercase text-fusion-forest text-left";

  // §7 — a parked column is greyed in place (not removed). Awarded tint wins.
  const cellBg = (supplierId) => {
    if (awardedSupplierId === supplierId) return 'bg-[#009159]/[0.06]';
    if (parkedIds.has(supplierId)) return 'bg-white opacity-40';
    return 'bg-white';
  };

  // A row is dimmed (§6) when Highlight differences is on and every column
  // shares the same comparable value — dim, never hide.
  const AttrRow = ({ label, cells, valueClass = '' }) => {
    const allSame = cells.length > 1 && cells.every(c => c.cmp === cells[0].cmp);
    const dim = highlight && allSame;
    return (
      <tr className={dim ? 'opacity-40' : ''}>
        <th scope="row" className={labelCol}>{label}</th>
        {cells.map((c) => (
          <td key={c.id} className={`px-4 py-3 text-sm border-b border-r border-line last:border-r-0 align-top ${cellBg(c.id)} ${c.className || ''} ${valueClass}`}>
            {c.node}
          </td>
        ))}
      </tr>
    );
  };

  const anyRating = pairs.some(p => p.recipient.suppliers?.rating_general != null);

  return (
    <table className="w-max border-collapse">
      <caption className="sr-only">Quote comparison for {brief?.brief_name || 'this brief'}</caption>
      <thead>
        <tr>
          {/* Corner — pinned both axes. Bottom-aligned so its content grounds
              the supplier cards above (they'd otherwise float beside a big
              empty white block) and sits just above the row labels below: a
              quote count + a colour key for the Cheapest / Earliest markers. */}
          <th scope="col" className="sticky left-0 top-0 z-30 bg-white w-[210px] min-w-[210px] border-b border-r border-line align-bottom p-4">
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-[26px] font-extrabold leading-none text-ink">{pairs.length}</p>
                <p className="text-[10px] font-bold tracking-[2px] uppercase text-[#51534A] mt-1.5">
                  Quote{pairs.length === 1 ? '' : 's'} to compare
                </p>
              </div>
              {(cheapestSupplierId || earliestSupplierId) && (
                <div className="flex flex-col gap-1.5 pt-3 border-t border-line">
                  {cheapestSupplierId && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#51534A]">
                      <span className="w-2 h-2 rounded-full bg-fusion-forest flex-shrink-0" /> Cheapest
                    </span>
                  )}
                  {earliestSupplierId && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#51534A]">
                      <span className="w-2 h-2 rounded-full bg-[#2E8FA1] flex-shrink-0" /> Earliest to confirm
                    </span>
                  )}
                </div>
              )}
            </div>
          </th>
          {pairs.map(({ recipient, response }) => {
            const supplier = recipient.suppliers || {};
            // Defined here rather than imported so the comparison rule stays
            // beside the thing it qualifies: a quote is stale when the brief has
            // moved past the version the supplier last saw. current_version is
            // absent on briefs that predate versioning — those can't be stale.
            const pricedAgainstOldVersion = (r) => {
              const now = Number(brief?.current_version);
              const seen = Number(r?.last_seen_version || 1);
              return Number.isFinite(now) && now > 1 && seen < now;
            };
            const sid = recipient.supplier_id;
            const isCheapest = sid === cheapestSupplierId;
            const isEarliest = sid === earliestSupplierId;
            const isAwardedSup = awardedSupplierId === sid;
            const isSelected = sid === selectedSupplierId;
            const chip = complianceChip(supplier.compliance_status);
            const reasons = reasonsBySupplier.get(sid) || [];
            const caveats = caveatCount(response.comments);
            const isParked = parkedIds.has(sid);
            const track = trackBySupplier.get(sid);
            const subtitle = track && track.count > 0
              ? `Worked with us ${track.count} time${track.count === 1 ? '' : 's'}${track.lastAwardAt && monthYear(track.lastAwardAt) ? ` · last ${monthYear(track.lastAwardAt)}` : ''}`
              : 'First time working with us';
            return (
              <th
                key={sid}
                scope="col"
                aria-selected={isSelected}
                className={`sticky top-0 z-20 w-[264px] min-w-[264px] text-left align-top px-4 py-4 border-b border-r border-line last:border-r-0 ${isAwardedSup ? 'bg-[#009159]/[0.06]' : 'bg-white'} ${isSelected ? 'shadow-[inset_0_2px_0_0_#92D400]' : ''}`}>
                {/* §7 — park / bring back (staff only). The header keeps full
                    opacity so the control stays legible; the parked column's
                    data cells grey out (cellBg). */}
                {isAdminUser && (
                  <div className="flex justify-end -mt-1 mb-1">
                    {isParked ? (
                      <span className="inline-flex items-center gap-1.5 text-[9px] font-bold tracking-[0.12em] uppercase">
                        <span className="bg-[#9BA08C]/15 text-[#51534A] border border-[#9BA08C]/40 rounded-full px-2 py-0.5">Parked</span>
                        <button type="button" onClick={() => onTogglePark(sid)} className="text-[#51534A] hover:text-ink underline underline-offset-2 bg-transparent border-0 cursor-pointer">Bring back</button>
                      </span>
                    ) : (
                      <button type="button" onClick={() => onTogglePark(sid)} className="text-[9px] font-bold tracking-[0.12em] uppercase text-[#9BA08C] hover:text-[#51534A] bg-transparent border-0 cursor-pointer">Park</button>
                    )}
                  </div>
                )}
                <div className={isParked ? 'opacity-40' : ''}>
                <label className="flex items-start gap-2 cursor-pointer mb-1">
                  <input
                    type="radio"
                    name="award-pick"
                    checked={isSelected}
                    onChange={() => onSelect(sid)}
                    className="mt-1 w-3.5 h-3.5 accent-active-green flex-shrink-0"
                    aria-label={`Select ${supplier.supplier_name || 'supplier'} for award`} />
                  <Link
                    to={`/briefs/${briefId}/responses/${sid}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-sm font-bold text-ink hover:text-active-green hover:underline underline-offset-2 transition-colors"
                    title={`Open ${supplier.supplier_name}'s response`}>
                    {supplier.supplier_name || 'Supplier'}
                  </Link>
                </label>
                {/* §5 — useful context replacing "Uncategorised". */}
                <p className="text-[10.5px] text-[#51534A] mb-2.5 leading-tight">
                  {subtitle}
                  {supplier.main_contact_name && <> · {supplier.main_contact_name}</>}
                </p>
                {/* §4 — commercially significant, and previously invisible.
                    A supplier who never resubmitted after an amendment is still
                    priced against the OLD brief, so their number is not
                    comparable with the others. Warn rather than exclude (Rob's
                    open question 3): a v1 quote is still information, and
                    hiding the column would look like the supplier never replied.
                    Sits directly ABOVE the price, because the price is the thing
                    it qualifies. */}
                {pricedAgainstOldVersion(recipient) && (
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#7A4E10] bg-[#E89A2E]/[0.14] border border-[#E89A2E]/40 rounded-md px-2 py-1 mb-1.5 leading-snug"
                    title={`Quoted against version ${recipient.last_seen_version || 1}; the brief is now version ${brief?.current_version}. They have not resubmitted since it changed.`}>
                    Priced against v{recipient.last_seen_version || 1} of {brief?.current_version}
                  </p>
                )}
                <p className={`text-2xl font-extrabold leading-none ${isCheapest ? 'text-fusion-forest' : 'text-ink'}`}>
                  {response.total_price != null ? formatGBP(response.total_price) : '—'}
                  <span className="text-[10px] font-normal text-[#51534A] ml-1">ex-VAT</span>
                </p>
                {/* §2 — price context. */}
                <BudgetPositionLine price={response.total_price} brief={brief} cheapestPrice={cheapestPrice} />

                {/* §1 compliance chip — text carries state. */}
                <div className="mt-2.5 flex flex-wrap gap-1.5 items-center">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-[9px] font-bold tracking-[0.12em] uppercase border ${CHIP_TONE[chip.tone]}`}>
                    {chip.label}
                  </span>
                  {isEarliest && (
                    <span className="inline-block bg-[#2E8FA1]/15 text-[#2E8FA1] border border-[#2E8FA1]/40 rounded-full px-2 py-0.5 text-[9px] font-bold tracking-[0.12em] uppercase">Earliest</span>
                  )}
                  {isAwardedSup && (
                    <span className="inline-block bg-[#009159]/15 text-[#009159] border border-[#009159]/40 rounded-full px-2 py-0.5 text-[9px] font-bold tracking-[0.12em] uppercase">Awarded</span>
                  )}
                  {/* §4 — caveat count. */}
                  {caveats > 0 && (
                    <span className="inline-flex items-center gap-1 bg-[#E89A2E]/15 text-[#9A6206] border border-[#E89A2E]/40 rounded-full px-2 py-0.5 text-[9px] font-bold tracking-[0.12em] uppercase">
                      {caveats} caveat{caveats === 1 ? '' : 's'}
                    </span>
                  )}
                  {/* Quoted before a material amendment (brief-amendments §4) —
                      priced against an earlier version, so not directly
                      comparable with quotes made against the current brief. */}
                  {response.amended_pending && (
                    <span
                      title={`This quote was submitted before the brief was amended${response.amended_since ? ` on ${formatDate(response.amended_since)}` : ''}. It may have been priced against an earlier version, so it isn't directly comparable — ask them to review and resubmit.`}
                      className="inline-flex items-center gap-1 bg-[#E0290A]/12 text-[#B71F06] border border-[#E0290A]/30 rounded-full px-2 py-0.5 text-[9px] font-bold tracking-[0.12em] uppercase">
                      Quoted pre-amendment
                    </span>
                  )}
                </div>

                {/* §1 — why blocked + route to fix. */}
                {chip.block && (
                  <div className="mt-2 text-[10.5px] leading-snug">
                    {reasons.length > 0 ? (
                      <ul className="space-y-0.5 mb-1">
                        {reasons.slice(0, 3).map(r => (
                          <li key={r.key} className="text-[#B71F06]">{r.detail}</li>
                        ))}
                        {reasons.length > 3 && <li className="text-[#51534A]">+{reasons.length - 3} more</li>}
                      </ul>
                    ) : (
                      <p className="text-[#B71F06] mb-1">Compliance blocked</p>
                    )}
                    <Link to={`/suppliers/${sid}`} onClick={(e) => e.stopPropagation()}
                      className="font-semibold text-active-green hover:underline underline-offset-2">
                      Fix compliance →
                    </Link>
                  </div>
                )}

                {/* Badges — another supplier attribute, inline. */}
                {(badgesBySupplier.get(sid) || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(badgesBySupplier.get(sid) || []).map(b => {
                      const cat = badgeCategoryMap.get(b.category_id);
                      if (!cat) return null;
                      return <BadgeChip key={`${b.category_id}-${b.tier}`} tier={b.tier} category={cat.name} />;
                    })}
                  </div>
                )}
                </div>
              </th>
            );
          })}
        </tr>
      </thead>

      <tbody>
        {/* §5 — Track record, above Commercials. */}
        <tr><th scope="colgroup" colSpan={pairs.length + 1} className={sectionRow}>Track record</th></tr>
        <AttrRow
          label="Briefs won"
          cells={pairs.map(({ recipient }) => {
            const t = trackBySupplier.get(recipient.supplier_id);
            const n = t?.count || 0;
            return { id: recipient.supplier_id, cmp: String(n), node: n > 0 ? `${n}` : 'None yet', className: n > 0 ? 'text-ink font-semibold' : 'text-[#51534A]' };
          })} />
        {/* Rating only where someone has actually rated them — never a zero
            for a supplier nobody has scored (§5). */}
        {anyRating && (
          <AttrRow
            label="Rating"
            cells={pairs.map(({ recipient }) => {
              const r = recipient.suppliers?.rating_general;
              return { id: recipient.supplier_id, cmp: r != null ? String(r) : '', node: r != null ? `${r}/5` : '—', className: r != null ? 'text-ink' : 'text-[#51534A]' };
            })} />
        )}

        {/* Commercials */}
        <tr><th scope="colgroup" colSpan={pairs.length + 1} className={sectionRow}>Commercials</th></tr>
        <AttrRow
          label="Subtotal (ex VAT)"
          valueClass="font-semibold text-ink"
          cells={pairs.map(({ recipient, response }) => ({
            id: recipient.supplier_id,
            cmp: String(response.total_price ?? ''),
            node: response.total_price != null ? formatGBP(response.total_price) : '—',
          }))} />
        <AttrRow
          label="VAT"
          cells={pairs.map(({ recipient, response }) => {
            const vatRate = Number(response.vat_rate) || 0;
            const vatAmt = (Number(response.total_price) || 0) * vatRate / 100;
            return {
              id: recipient.supplier_id,
              cmp: `${vatAmt}-${vatRate}`,
              node: <span className="text-fusion-mid-grey">{formatGBP(vatAmt)} <span className="text-[10px] text-[#51534A]">({vatRate}%)</span></span>,
            };
          })} />
        <AttrRow
          label="Total (inc VAT)"
          cells={pairs.map(({ recipient, response }) => {
            const isCheapest = recipient.supplier_id === cheapestSupplierId;
            const vatRate = Number(response.vat_rate) || 0;
            const gross = (Number(response.total_price) || 0) * (1 + vatRate / 100);
            return {
              id: recipient.supplier_id,
              cmp: String(Math.round(gross * 100)),
              node: gross ? formatGBP(gross) : '—',
              className: isCheapest ? 'text-fusion-forest font-bold' : 'font-semibold text-ink',
            };
          })} />
        <AttrRow
          label="Payment terms"
          cells={pairs.map(({ recipient, response }) => ({
            id: recipient.supplier_id,
            cmp: `${response.deposit_pct ?? ''}|${response.deposit_trigger ?? ''}|${response.balance_terms ?? ''}|${response.penalty_pct_per_day ?? ''}|${response.payment_schedule ?? ''}`,
            node: <PaymentTermsCell response={response} />,
          }))} />

        {/* Terms */}
        <tr><th scope="colgroup" colSpan={pairs.length + 1} className={sectionRow}>Terms</th></tr>
        <AttrRow
          label="Confirmation Required By"
          cells={pairs.map(({ recipient, response }) => {
            const isEarliest = recipient.supplier_id === earliestSupplierId;
            return {
              id: recipient.supplier_id,
              cmp: response.deadline_date || '',
              node: formatDate(response.deadline_date),
              className: isEarliest && response.deadline_date ? 'text-fusion-forest font-bold' : 'text-fusion-mid-grey',
            };
          })} />
        <AttrRow
          label="Quote Type"
          cells={pairs.map(({ recipient, response }) => ({
            id: recipient.supplier_id,
            cmp: `${response.is_itemised}-${response.line_items?.length || 0}`,
            node: (
              <span className="text-fusion-mid-grey">
                {response.is_itemised ? 'Itemised' : 'Lump-sum'}
                {response.is_itemised && response.line_items && (
                  <span className="text-[10px] text-[#51534A] ml-1">· {response.line_items.length} line{response.line_items.length === 1 ? '' : 's'}</span>
                )}
              </span>
            ),
          }))} />

        {/* Notes */}
        <tr><th scope="colgroup" colSpan={pairs.length + 1} className={sectionRow}>Notes</th></tr>
        <AttrRow
          label="Salient Points"
          cells={pairs.map(({ recipient, response }) => ({
            id: recipient.supplier_id,
            cmp: response.salient_points || '',
            node: response.salient_points
              ? <span className="text-fusion-mid-grey whitespace-pre-wrap text-xs leading-relaxed">{response.salient_points}</span>
              : <span className="text-[#51534A] italic">—</span>,
          }))} />
        {/* §4 — caveats never truncated; a warning icon (with text
            equivalent) flags rows that carry them. */}
        <AttrRow
          label="Comments / Caveats"
          cells={pairs.map(({ recipient, response }) => {
            const has = caveatCount(response.comments) > 0;
            return {
              id: recipient.supplier_id,
              cmp: response.comments || '',
              node: response.comments ? (
                <div className="flex items-start gap-1.5">
                  {has && (
                    <span className="text-[#E89A2E] flex-shrink-0 mt-0.5" aria-hidden="true">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                    </span>
                  )}
                  {has && <span className="sr-only">Caveat: </span>}
                  <span className="text-fusion-mid-grey whitespace-pre-wrap text-xs leading-relaxed">{response.comments}</span>
                </div>
              ) : <span className="text-[#51534A]">None</span>,
            };
          })} />
        <AttrRow
          label="Attachments"
          cells={pairs.map(({ recipient, response }) => {
            const count = (response.attachments || []).length || (response.supporting_doc_url ? 1 : 0);
            return {
              id: recipient.supplier_id,
              cmp: String(count),
              node: count > 0 ? `${count} attached` : 'None',
              className: count > 0 ? 'text-ink' : 'text-[#51534A]',
            };
          })} />
        {/* §7 — internal working note per supplier (staff only). Not routed
            through AttrRow so it never dims/tints; it's an editable aid. */}
        {isAdminUser && (
          <tr>
            <th scope="row" className={labelCol}>
              Working note
              <span className="block font-normal normal-case tracking-normal text-[9px] text-[#9BA08C] mt-0.5">Internal only</span>
            </th>
            {pairs.map(({ recipient }) => (
              <td key={`note-${recipient.supplier_id}`} className={`px-4 py-3 border-b border-r border-line last:border-r-0 align-top ${cellBg(recipient.supplier_id)}`}>
                <WorkingNoteCell
                  value={notesBySupplier.get(recipient.supplier_id)?.working_note || ''}
                  onSave={(v) => onSaveNote(recipient.supplier_id, { working_note: v })} />
              </td>
            ))}
          </tr>
        )}

        {/* Submission */}
        <tr><th scope="colgroup" colSpan={pairs.length + 1} className={sectionRow}>Submission</th></tr>
        <AttrRow
          label="Submitted"
          cells={pairs.map(({ recipient, response }) => ({
            id: recipient.supplier_id,
            cmp: response.submitted_at || response.created_at || '',
            node: formatDate(response.submitted_at || response.created_at),
            className: 'text-fusion-mid-grey',
          }))} />
      </tbody>
    </table>
  );
}

// ── Sticky award bar (§9) ────────────────────────────────────────────────
function AwardBar({
  pair, brief, cheapestPrice, isAwarded, awardedSupplierId, reasonsBySupplier,
  overriddenIds, overrideDraft, setOverrideDraft, savingOverride, onConfirmOverride, onAward,
}) {
  const supplier = pair.recipient.suppliers || {};
  const sid = pair.recipient.supplier_id;
  const price = pair.response.total_price;
  const chip = complianceChip(supplier.compliance_status);
  const blocked = chip.block && !overriddenIds.has(sid);
  const reasons = reasonsBySupplier.get(sid) || [];
  const reasonText = reasons[0]?.detail || 'compliance blocked';
  const pos = budgetPosition(price, brief);
  const isThisAwarded = awardedSupplierId === sid;
  const drafting = overrideDraft && overrideDraft.supplierId === sid;
  const reasonId = `award-block-reason-${sid}`;

  return (
    <div className="flex-shrink-0 border-t border-line bg-white px-8 py-3.5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] font-bold tracking-[2px] uppercase text-[#51534A] mb-0.5">Selected</p>
          <p className="text-sm font-bold text-ink truncate">
            {supplier.supplier_name || 'Supplier'}
            <span className="text-fusion-mid-grey font-semibold ml-2">{price != null ? formatGBP(price) : '—'} <span className="text-[10px] font-normal text-[#51534A]">ex-VAT</span></span>
            {pos && (
              <span className={`ml-2 text-[11px] font-semibold ${pos.direction === 'under' && pos.pct >= 60 ? 'text-[#9A6206]' : 'text-[#51534A]'}`}>
                {pos.pct}% {pos.direction} budget
              </span>
            )}
            {cheapestPrice != null && price != null && Number(price) !== cheapestPrice && (
              <span className="ml-2 text-[11px] text-[#51534A]">+{formatGBP(Number(price) - cheapestPrice)} vs cheapest</span>
            )}
          </p>
          {pair.response.amended_pending && (
            <p className="text-[11px] font-semibold text-[#B71F06] mt-0.5">⚠ Quoted before the brief was amended — may be priced against an earlier version.</p>
          )}
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {isAwarded ? (
            isThisAwarded ? (
              <span className="inline-flex items-center gap-1.5 bg-[#009159]/15 text-[#009159] border border-[#009159]/40 rounded-xl px-4 py-2.5 text-[11px] font-bold tracking-[2px] uppercase">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                Awarded
              </span>
            ) : (
              <button type="button" disabled title="Brief already awarded — re-open from the brief page to switch suppliers"
                className="bg-white border border-line text-[#51534A] text-[11px] font-bold tracking-[2px] uppercase rounded-xl px-4 py-2.5 cursor-not-allowed">
                Brief already awarded
              </button>
            )
          ) : blocked ? (
            <>
              <div className="text-right">
                <p id={reasonId} className="text-[11px] font-semibold text-[#B71F06] leading-tight first-letter:uppercase">{reasonText}</p>
                <button type="button" onClick={() => setOverrideDraft({ supplierId: sid, reason: '' })}
                  className="text-[11px] font-bold text-fusion-mid-grey hover:text-ink underline underline-offset-2 bg-transparent border-0 cursor-pointer">
                  Override…
                </button>
              </div>
              <button type="button" disabled aria-describedby={reasonId}
                className="bg-[#E0290A]/10 text-[#B71F06] border border-[#E0290A]/30 text-[11px] font-bold tracking-[2px] uppercase rounded-xl px-4 py-2.5 cursor-not-allowed">
                Award blocked
              </button>
            </>
          ) : (
            <button type="button" onClick={() => onAward(sid, overriddenIds.has(sid) ? (reasons[0]?.detail || 'compliance override') : null)}
              className="bg-[#92D400] text-[#131313] text-[11px] font-bold tracking-[2px] uppercase rounded-xl px-5 py-2.5 hover:bg-[#a4e810] transition-colors">
              {overriddenIds.has(sid) ? 'Award (overridden)' : `Award to ${supplier.supplier_name || 'supplier'}`}
            </button>
          )}
        </div>
      </div>

      {/* §1 override composer — reason mandatory; writes a compliance audit
          entry before the award is unlocked. */}
      {drafting && (
        <div className="mt-3 pt-3 border-t border-line">
          <label className="block text-[10px] font-bold tracking-[2px] uppercase text-[#51534A] mb-1.5">
            Reason for overriding the compliance block
          </label>
          <div className="flex items-start gap-2">
            <textarea
              value={overrideDraft.reason}
              onChange={(e) => setOverrideDraft({ supplierId: sid, reason: e.target.value })}
              rows={2}
              autoFocus
              placeholder="Why is it acceptable to award this supplier despite the compliance block? This is recorded."
              className="flex-1 border border-line rounded-xl px-3 py-2 text-sm text-ink placeholder:text-[#9BA08C] focus:outline-none focus:border-active-green resize-y" />
            <div className="flex flex-col gap-2 flex-shrink-0">
              <button type="button" disabled={!overrideDraft.reason.trim() || savingOverride} onClick={onConfirmOverride}
                className="bg-[#131313] text-white text-[11px] font-bold tracking-[1px] uppercase rounded-xl px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-black transition-colors whitespace-nowrap">
                {savingOverride ? 'Saving…' : 'Override & continue'}
              </button>
              <button type="button" onClick={() => setOverrideDraft(null)}
                className="text-[11px] font-bold text-fusion-mid-grey hover:text-ink bg-transparent border border-line rounded-xl px-4 py-2">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
